<template>
  <main class="mb-10 md:mb-20">
    <cms-section v-if="eSpots?.[0]" :section="eSpots?.[0]" />
    <h1 class="mb-10 text-10 fw-medium lh-tight">
      {{ pageHeading }}
    </h1>
    <h2 v-html="header.heading" class="mb-2 mt-10 text-2xl fw-medium lh-tight" />
    <p v-html="header.paragraph" />
    <div class="my-10">
      <div
        v-for="({ bullets,
                  code,
                  heading,
                  icon,
                  isLowImpact,
                  signUpBullet,
                  vatIncluded }, key) in deliveryTypes"
        :key
        v-style="{
          'gap': { sm: '1.25rem', lg: '1.9rem' },
          'grid-cols': { md: '1fr', lg: '3.75rem 1fr 9.4rem' },
        }"
        class="grid b-t py-5"
        :class="{ 'b-b': key === deliveryTypes.length - 1 }"
      >
        <div class="flex justify-center">
          <img alt="" class="h-8" role="presentation" :src="icon">
        </div>
        <div class="relative">
          <h3 v-html="getDeliveryMethod(code)?.label || heading" class="fw-medium" />
          <ul class="list-disc ws-pre-line" style="padding-inline-start: 1.25rem">
            <li v-for="(bullet, i) in bullets" :key="i" class="my-2 lh-tight">
              <base-interpolator :content="bullet">
                <template #threshold v-if="$feature.deliveryShippingThresholds[code]?.threshold">
                  {{ useFormattedPrice($feature.deliveryShippingThresholds[code].threshold, useCurrencyCode()) }}
                </template>
                <template #bold="{ args: [message] }">
                  <span class="fw-medium">{{ message }}</span>
                </template>
              </base-interpolator>
            </li>
            <!-- Threshold message as last list item only If it's a loyalty locale, 'pudo' code, threshold available -->
            <li
              v-if="$feature.enableLoyaltyFeatures
                && code === 'pudo'
                && $feature.deliveryShippingThresholds[code].threshold"
            >
              <base-interpolator :content="priceMessages.threshold">
                <template #threshold v-if="$feature.deliveryShippingThresholds[code]?.threshold">
                  {{ useFormattedPrice($feature.deliveryShippingThresholds[code].threshold, useCurrencyCode()) }}
                </template>
              </base-interpolator>
            </li>
            <li v-if="signUpBullet" class="mb-2 lh-tight">
              <base-interpolator :content="signUpBullet">
                <template #sign-up="{ args: [message] }">
                  <vf-link
                    class="uppercase"
                    to="/sign-up"
                    @click.prevent.capture="DialogSignIn.open({
                      formLocation: 'modal:single:body:none',
                    })"
                  >
                    {{ message }}
                  </vf-link>
                </template>
              </base-interpolator>
            </li>
          </ul>
          <div v-if="isLowImpact" class="right-0 top-0 mt-5 flex lg:absolute lg:mt-0">
            <img alt="" class="mr-3" role="presentation" :src="lowImpact.icon" width="20">
            <p v-html="lowImpact.text" class="text-3" />
          </div>
        </div>
        <div class="lg:text-right">
          <!-- Shows FREE or formatted valid amount -->
          <div v-if="!$feature.deliveryShippingThresholds[code].amount" v-html="$t.free" class="fw-medium" />
          <!-- Shows price -->
          <div v-else class="fw-medium">
            {{ useFormattedPrice($feature.deliveryShippingThresholds[code].amount, useCurrencyCode()) }}
            <span v-if="vatIncluded" v-html="priceMessages.vatIncluded" />
          </div>
          <!-- XPLR message only If it's a loyalty locale and 'pudo' code -->
          <div v-if="$feature.enableLoyaltyFeatures && code === 'pudo'" v-html="priceMessages.xplr" />
          <!-- Shows threshold (if available)  -->
          <base-interpolator
            v-else-if="$feature.deliveryShippingThresholds[code].threshold"
            :content="priceMessages.threshold"
          >
            <template #threshold v-if="$feature.deliveryShippingThresholds[code]?.threshold">
              {{ useFormattedPrice($feature.deliveryShippingThresholds[code].threshold, useCurrencyCode()) }}
            </template>
          </base-interpolator>
        </div>
      </div>
    </div>
    <p
      v-for="(disclaimer, key) in deliveryDisclaimers"
      :key
      v-html="disclaimer"
      class="mb-2"
    />
    <h2 class="mb-2 mt-10 text-2xl fw-medium lh-tight">
      {{ notes.heading }}
    </h2>
    <ul class="mb-2 list-disc" style="padding-inline-start: 1.25rem">
      <li v-for="(item, key) in notes.list" :key v-html="item" />
    </ul>
    <p v-html="notes.paragraph" />
    <h2 v-html="taxes.heading" class="mb-2 mt-10 text-2xl fw-medium lh-tight" />
    <p v-html="taxes.paragraph" />
  </main>
</template>

<script lang="ts" setup>
const emit = defineEmits<{
  'change-page-title': [title: string]
}>()
const { data, error } = await useApi().internal.getStaticPage('help/delivery')
const eSpots = (await useCms().getSections.generic('delivery')).data.value?.content.sections
if (!data.value) throw createError({ ...error.value, fatal: true })
const cart = useCartStore()

const getDeliveryMethod = (c: string) =>
  cart.defaultShippingMethod?.applicableShippingMethods?.find(({ code }) => c === code)

const { DialogSignIn } = useDialogsStore()

const {
  deliveryDisclaimers,
  deliveryTypes,
  header,
  lowImpact,
  pageHeading,
  notes,
  taxes,
  priceMessages
} = data?.value
emit('change-page-title', pageHeading)
</script>
